import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { portfolioData } from '../../data/portfolio';
import BackgroundSection from '../common/BackgroundSection';
import YouTubePreview from '../common/YouTubePreview';

const AchievementsContainer = styled.section`
  position: relative;
  min-height: 100vh;
  padding: 100px 0;
  background: #071018;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 60px;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
`;

const AchievementCard = styled(motion.div)<{ $fullWidth?: boolean }>`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  ${({ $fullWidth }) => $fullWidth && 'grid-column: 1 / -1;'}

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const AchievementTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 15px;
`;

const AchievementDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-bottom: 20px;
`;

const AchievementMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AchievementDate = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const AchievementPrize = styled.span`
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #000;
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const LinkedInPost = styled.div`
  background: var(--clr-surface-bg);
  border-radius: 15px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid var(--clr-surface-border);
`;

const LinkedInHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
`;

const LinkedInLogo = styled.div`
  width: 24px;
  height: 24px;
  background: #0077B5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
`;

const LinkedInText = styled.span`
  color: var(--clr-text-bright);
  font-size: 0.9rem;
`;

const LinkedInImage = styled.img<{ $position?: string }>`
  width: 100%;
  max-height: 280px;
  object-fit: cover;
  object-position: ${({ $position }) => $position || 'center'};
  border-radius: 10px;
  margin-top: 10px;
`;

const ExternalLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const Achievements: React.FC = () => {
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0 
    }
  };

  return (
    <AchievementsContainer id="achievements">
      <BackgroundSection
        backgroundImage="/images/action-with-africa.jpg"
        opacity={0.85}
      />
      
      <Container>
        <Title
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {t('achievements.sectionTitle')}
        </Title>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.2 }}
        >
          <AchievementsGrid>
            {portfolioData.achievements.map((achievement, index) => (
              <AchievementCard
                key={index}
                variants={cardVariants}
                whileHover={{ scale: 1.02 }}
                $fullWidth
              >
                <AchievementTitle>{t(`achievements.entries.${achievement.id}.title`, achievement.title)}</AchievementTitle>
                
                <AchievementMeta>
                  <AchievementDate>{achievement.date}</AchievementDate>
                  {achievement.prize && (
                    <AchievementPrize>{achievement.prize}</AchievementPrize>
                  )}
                </AchievementMeta>

                <AchievementDescription>
                  {t(`achievements.entries.${achievement.id}.description`, achievement.description)}
                </AchievementDescription>

                {/* YouTube Preview for Action With Africa */}
                {achievement.id === 'action-africa' && achievement.links && (
                  (() => {
                    const videoLink = achievement.links.find(link => link.type === 'video');
                    return videoLink ? (
                      <YouTubePreview 
                        videoUrl={videoLink.url}
                        title="Action With Africa Challenge Final Presentation"
                      />
                    ) : null;
                  })()
                )}

                {achievement.linkedinPost && (
                  <LinkedInPost>
                    <LinkedInHeader>
                      <LinkedInLogo>in</LinkedInLogo>
                      <LinkedInText>{t('achievements.featuredOnLinkedIn')}</LinkedInText>
                    </LinkedInHeader>
                    <LinkedInText>{achievement.linkedinPost.text}</LinkedInText>
                    {achievement.linkedinPost.image && (
                      <LinkedInImage 
                        src={achievement.linkedinPost.image} 
                        alt="LinkedIn post"
                        $position={achievement.linkedinPost.imagePosition}
                      />
                    )}
                  </LinkedInPost>
                )}

                {achievement.links && achievement.links.length > 0 && (
                  <ExternalLink
                    href={achievement.links[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('achievements.learnMore')}
                  </ExternalLink>
                )}
              </AchievementCard>
            ))}
          </AchievementsGrid>
        </motion.div>
      </Container>
    </AchievementsContainer>
  );
};

export default Achievements;